import { IContact, IAddress } from "pages/companies/types";
import Table from "components/Table/index";
import { titleConvert, OnlyAddress } from './../../../../helpers/api/utils';
import { ColumnDef } from "@tanstack/table-core";
import BlurSpan from 'components/BlurSpan';
import { useAuth } from "providers/CustomAuthProvider";
import useGlobalPremiumFlag from "hooks/useGlobalPremiumFlag";
type ContactsDataProps = {
    contacts?: IContact[];
    hiddenColumns?: string[];
    enableActions?: boolean;
    loading?: boolean;
    total?: number;
    hook?: any
};

type AddressDataProps = {
    addresses?: IContact[];
    hiddenColumns?: string[];
    enableActions?: boolean;
    loading?: boolean;
    total?: number;
    hook?: any
};

const getContactColumns = (isPremium: boolean): ColumnDef<IContact>[] => [
    {
        header: "License State",
        accessorKey: "stateCode",
        enableSorting: false,
        cell: (props) => <span style={{textTransform: 'uppercase'}}>{`${titleConvert(props.row.original.stateCode ?? "")}`}</span>
    }
    ,{
        header: "Full Name",
        accessorKey: "contactName",
        enableSorting: false,
        cell: (props) => {
            const rawName = props.row.original.contactName ?? "";
            const formatted = titleConvert(rawName);

            if (!formatted) {
                return <span />;
            }

            // Split on spaces to handle first/last name separately
            const parts = formatted.split(" ");

            return (
                <span>
                    {parts.map((part, idx) => {
                        if (!part) return null;
                        const firstChar = part.charAt(0);
                        const rest = part.slice(1);
                        return (
                            <span key={idx}>
                                {idx > 0 && " "}
                                {firstChar}
                                {rest && (
                                    <BlurSpan isBlur={!isPremium}>
                                        <span>{rest}</span>
                                    </BlurSpan>
                                )}
                            </span>
                        );
                    })}
                </span>
            );
        },
    },
    {
        header: "Email",
        accessorKey: "contactEmail",
        enableSorting: false,
        cell: (props) => (
            <BlurSpan isBlur={!isPremium}>
                <span>{props.row.original.contactEmail ?? ""}</span>
            </BlurSpan>
        )
    },
    {
        header: "Phone",
        accessorKey: "contactPhone",
        enableSorting: false,
        cell: (props) => (
            <BlurSpan isBlur={!isPremium}>
                <span>{props.row.original.contactPhone ?? ""}</span>
            </BlurSpan>
        )
    },
    {
        header: "Role",
        accessorKey: "contactRole",
        enableSorting: false,
        cell: (props) => <span>{`${titleConvert(props.row.original.contactRole ?? "")}`}</span>
    }
];

const getBaseAddressColumns = (isPremium: boolean): ColumnDef<IAddress>[] => [
    {
        header: "License State",
        accessorKey: "licenseStateCode",
        enableSorting: false,
        cell: (props) => <span style={{textTransform: 'uppercase'}}>{`${titleConvert(props.row.original.licenseStateCode ?? "")}`}</span>
    },
    {
        header: "Address",
        accessorKey: "contactAddress",
        enableSorting: false,
        cell: (props) => (
            <BlurSpan isBlur={!isPremium}>
                <span>
                    {OnlyAddress(
                        props.row.original.contactAddress ?? "",
                        props.row.original.contactCity ?? "",
                        props.row.original.stateCode ?? "",
                        props.row.original.contactZip ?? ""
                    )}
                </span>
            </BlurSpan>
        )
    },
    {
        header: "City",
        accessorKey: "contactCity",
        enableSorting: false,
        cell: (props) => <span>{`${titleConvert(props.row.original.contactCity ?? "")}`}</span>
    },
    {
        header: "State",
        accessorKey: "stateCode",
        enableSorting: false,
        cell: (props) => <span style={{textTransform: 'uppercase'}}>{`${titleConvert(props.row.original.stateCode ?? "")}`}</span>
    },
    {
        header: "Zipcode",
        accessorKey: "contactZip",
        enableSorting: false,
        cell: (props) => <span>{props.row.original.contactZip ?? ""}</span>
    }
];

export const ContactsDataTable = ({
    contacts,
    loading,
}: ContactsDataProps) => {
    const isPremium = useGlobalPremiumFlag();
    const contactColumns = getContactColumns(isPremium);

    return (
        <>
            <Table<IContact> columns={contactColumns} data={contacts} loading={loading} enableSearch={false} enablePagination={false} enableHideColumns={false} />
        </>
    );
};

export const AddressDataTable = ({
    addresses,
    loading,
}: AddressDataProps) => {
    const { isSystemAdmin } = useAuth();
    const isPremium = useGlobalPremiumFlag();
    const baseAddressColumns = getBaseAddressColumns(isPremium);
    const addressColumns: ColumnDef<IAddress>[] = isSystemAdmin
        ? [
            ...baseAddressColumns,
            {
                header: "USPS",
                accessorKey: "isAddressParsed",
                enableSorting: false,
                cell: (props) => {
                    const isParsed = props.row.original.isAddressParsed;

                    return (
                        <span
                            style={{ fontSize: "10px" }}
                            className={`px-2 font-monospace badge bg-${isParsed ? "info" : "warning"}`}
                        >
                            {isParsed ? "Parsed" : "Failed"}
                        </span>
                    );
                }
            }
        ]
        : baseAddressColumns;

    return (
        <>
            <Table<IAddress> columns={addressColumns} data={addresses} loading={loading} enableSearch={false} enablePagination={false} enableHideColumns={false} />
        </>
    );
};

